/**
 * NoContactTab — "Nothing Falls Through The Cracks" view.
 *
 * Surfaces loans whose `last_contact_at` (or settlement_date as fallback)
 * is older than N days. The CSM sweeps this once a week to make sure no
 * client has slipped through silence.
 *
 * Click row → open LoanDrawer. The "Mark contacted" button logs a quick
 * "touch base" note on the loan timeline, which updates last_contact_at.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Phone, Mail, Building2, AlertTriangle, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
};

const NoContactTab = ({ onOpenLoan, onLoanTouched }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(60);
  const [scope, setScope] = useState('team');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), mine_only: scope === 'mine' ? 'true' : 'false' });
      const r = await fetch(`${backendUrl}/api/settled-loans/care/no-contact?${params}`, { headers: authHeaders });
      if (r.ok) setLoans(await r.json());
    } finally { setLoading(false); }
  }, [backendUrl, authHeaders, days, scope]);

  useEffect(() => { load(); }, [load]);

  const markContacted = async (loanId, channel) => {
    setBusyId(loanId);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/${loanId}/activities`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: channel, content: `Quick touch base — ${channel}` }),
      });
      if (!r.ok) throw new Error('Failed');
      toast.success('Contact logged');
      setLoans((prev) => prev.filter((l) => l.id !== loanId));
      onLoanTouched?.();
    } catch (e) { toast.error(e.message); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="no-contact-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[{ k: 'team', label: 'All' }, { k: 'mine', label: 'Mine' }].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setScope(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                scope === k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              data-testid={`nocontact-scope-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500 mr-1">Stale &gt;</span>
          {[30, 60, 90, 180].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                days === d ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-slate-800 text-slate-600 border-slate-200 dark:border-slate-700 hover:border-amber-300'
              }`}
              data-testid={`nocontact-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
        ) : loans.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">
            <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 dark:text-slate-200 mb-1">Nothing fell through</p>
            <p className="text-xs">Every loan has been touched in the last {days} days.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {loans.map((l) => (
              <li key={l.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid="no-contact-row">
                <div className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 grid place-items-center shrink-0">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => onOpenLoan?.(l.id)}
                      className="font-semibold text-slate-900 dark:text-white hover:text-blue-600 text-sm truncate"
                    >
                      {l.client_name}
                    </button>
                    <span className="font-mono text-[10px] text-slate-400">{l.loan_number}</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                      {l.days_since_contact != null ? `${l.days_since_contact}d` : '—'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <Building2 className="w-3 h-3" /> {l.lender} · {fmtMoney(l.loan_amount)} · settled {fmtDate(l.settlement_date)}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    Last touch: {l.last_contact_at ? fmtDate(l.last_contact_at) : 'never since settlement'}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {l.client_phone && (
                    <button
                      type="button"
                      disabled={busyId === l.id}
                      onClick={() => markContacted(l.id, 'call')}
                      title={`Log call to ${l.client_phone}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                      data-testid="log-call-btn"
                    >
                      <Phone className="w-3 h-3" /> Called
                    </button>
                  )}
                  {l.client_email && (
                    <button
                      type="button"
                      disabled={busyId === l.id}
                      onClick={() => markContacted(l.id, 'email')}
                      title={`Log email to ${l.client_email}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-50"
                      data-testid="log-email-btn"
                    >
                      <Mail className="w-3 h-3" /> Emailed
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NoContactTab;
